import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  X, Upload, FileText, User, Phone, MapPin, DollarSign, 
  HeartHandshake, Briefcase, CheckCircle, ChevronDown, ChevronUp,
  Shield, FileCheck, Camera, Calendar, AlertTriangle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { workersAPI, onboardingAPI } from '../../services/api';

const AddWorkerModal = ({ onClose, onSuccess }) => {
  const [advancedMode, setAdvancedMode] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Basic info
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    service_types: [],
    hourly_rate: 20,
    experience_years: 0,
    is_verified: false,
    start_onboarding: true, // Start onboarding by default
    // Advanced fields
    emergency_contact_name: '',
    emergency_contact_phone: '',
    date_of_birth: '',
    ssn_last4: '',
    certifications: '',
    languages: '',
    bio: '',
    availability_notes: ''
  });
  
  // Document uploads
  const [documents, setDocuments] = useState({
    id_document: null,
    certification: null,
    background_check: null
  });
  
  const [uploadedDocs, setUploadedDocs] = useState({
    id_document: null,
    certification: null,
    background_check: null
  });

  const toggleServiceType = (type) => {
    setFormData(prev => ({
      ...prev,
      service_types: prev.service_types.includes(type)
        ? prev.service_types.filter(t => t !== type)
        : [...prev.service_types, type]
    }));
  };

  const handleFileSelect = (field, file) => {
    setDocuments(prev => ({ ...prev, [field]: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.service_types.length === 0) {
      toast.error('Please select at least one service type');
      return;
    }

    setLoading(true);
    
    try {
      // Prepare data - convert certifications string to array
      const submitData = {
        ...formData,
        certifications: formData.certifications 
          ? formData.certifications.split(',').map(c => c.trim()).filter(c => c)
          : []
      };
      
      // Create worker
      const res = await workersAPI.createManual(submitData);
      const workerId = res.data.user_id || res.data.id;
      
      // Upload documents if any
      if (documents.id_document || documents.certification || documents.background_check) {
        for (const [docType, file] of Object.entries(documents)) {
          if (file) {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            formDataUpload.append('document_type', docType);
            try {
              await onboardingAPI.ownerUploadDocument(workerId, formDataUpload);
            } catch (err) {
              console.error(`Failed to upload ${docType}:`, err);
            }
          }
        }
      }
      
      const onboardingMsg = res.data.onboarding_started 
        ? ' Onboarding started - worker will need to complete required documents.'
        : '';
      toast.success(`Worker created successfully!${onboardingMsg}`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create worker');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="modal-content max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold">Add New Worker</h2>
            <p className="text-[var(--text-muted)] text-sm">Create a complete worker profile</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--background-subtle)] rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="mb-6 p-3 bg-[var(--background-subtle)] rounded-lg flex items-center justify-between">
          <span className="text-sm font-medium">Advanced Mode (Documents, Emergency Contact, etc.)</span>
          <Button 
            type="button"
            variant={advancedMode ? "default" : "outline"}
            size="sm"
            onClick={() => setAdvancedMode(!advancedMode)}
            className={advancedMode ? "bg-[var(--primary)]" : ""}
          >
            {advancedMode ? "On" : "Off"}
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2 text-[var(--primary)]">
              <User className="w-5 h-5" />
              Basic Information
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="input-styled mt-1"
                  required
                  data-testid="worker-name"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-styled mt-1"
                  required
                  data-testid="worker-email"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Phone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-styled mt-1"
                  required
                  data-testid="worker-phone"
                />
              </div>
              {advancedMode && (
                <div>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="input-styled mt-1"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2 text-[var(--primary)]">
              <MapPin className="w-5 h-5" />
              Address
            </h3>
            
            <div>
              <Label>Street Address *</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="input-styled mt-1"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>City *</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="input-styled mt-1"
                  required
                />
              </div>
              <div>
                <Label>State *</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="input-styled mt-1"
                  required
                />
              </div>
              <div>
                <Label>ZIP Code *</Label>
                <Input
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  className="input-styled mt-1"
                  required
                />
              </div>
            </div>
          </div>

          {/* Emergency Contact - Advanced */}
          {advancedMode && (
            <div className="space-y-4 p-4 bg-[var(--warning)]/10 rounded-xl border border-[var(--warning)]/30">
              <h3 className="font-semibold flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5" />
                Emergency Contact
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    value={formData.emergency_contact_name}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                    className="input-styled mt-1"
                    placeholder="Emergency contact full name"
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    value={formData.emergency_contact_phone}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                    className="input-styled mt-1"
                    placeholder="Emergency contact phone"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Service Types */}
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2 text-[var(--primary)]">
              <Briefcase className="w-5 h-5" />
              Service Types *
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <label className={`cursor-pointer p-3 rounded-xl border-2 text-center transition-all ${
                formData.service_types.includes('rides') ? 'border-[#8B5CF6] bg-[#8B5CF6]/10' : 'border-[var(--border-primary)]'
              }`}>
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={formData.service_types.includes('rides')}
                  onChange={() => toggleServiceType('rides')}
                />
                <svg className="w-7 h-7 mx-auto mb-2 text-[#8B5CF6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8M5 11h14M7 15h10M9 19l1-4h4l1 4" />
                </svg>
                <span className="font-semibold text-sm">Rides</span>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">Driver</p>
              </label>
              <label className={`cursor-pointer p-3 rounded-xl border-2 text-center transition-all ${
                formData.service_types.includes('laundry') ? 'border-[#F4C2C2] bg-[#F4C2C2]/10' : 'border-[var(--border-primary)]'
              }`}>
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={formData.service_types.includes('laundry')}
                  onChange={() => toggleServiceType('laundry')}
                />
                <svg className="w-7 h-7 mx-auto mb-2 text-[#F4C2C2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
                <span className="font-semibold text-sm">Laundry</span>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">Wash/Dry</p>
              </label>
              <label className={`cursor-pointer p-3 rounded-xl border-2 text-center transition-all ${
                formData.service_types.includes('courier') ? 'border-blue-500 bg-blue-500/10' : 'border-[var(--border-primary)]'
              }`}>
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={formData.service_types.includes('courier')}
                  onChange={() => toggleServiceType('courier')}
                />
                <svg className="w-7 h-7 mx-auto mb-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8M8 11h8M8 15h5m-9 5h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="font-semibold text-sm">Courier</span>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">Delivery</p>
              </label>
              <label className={`cursor-pointer p-3 rounded-xl border-2 text-center transition-all ${
                formData.service_types.includes('errands') ? 'border-[#EC4899] bg-[#EC4899]/10' : 'border-[var(--border-primary)]'
              }`}>
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={formData.service_types.includes('errands')}
                  onChange={() => toggleServiceType('errands')}
                />
                <svg className="w-7 h-7 mx-auto mb-2 text-[#EC4899]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="font-semibold text-sm">Errands</span>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">Tasks</p>
              </label>
            </div>
            <p className="text-xs text-[var(--text-muted)]">
              Select all service types this worker can perform. NO CNA/nursing services available.
            </p>
          </div>

          {/* Pay & Experience */}
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2 text-[var(--primary)]">
              <DollarSign className="w-5 h-5" />
              Pay & Experience
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Hourly Rate ($) *</Label>
                <Input
                  type="number"
                  min="10"
                  max="100"
                  step="0.50"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) })}
                  className="input-styled mt-1"
                  required
                />
              </div>
              <div>
                <Label>Years of Experience</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.experience_years}
                  onChange={(e) => setFormData({ ...formData, experience_years: parseInt(e.target.value) || 0 })}
                  className="input-styled mt-1"
                />
              </div>
            </div>

            {advancedMode && (
              <>
                <div>
                  <Label>Certifications</Label>
                  <Input
                    value={formData.certifications}
                    onChange={(e) => setFormData({ ...formData, certifications: e.target.value })}
                    className="input-styled mt-1"
                    placeholder="CPR, First Aid, etc."
                  />
                </div>
                <div>
                  <Label>Languages Spoken</Label>
                  <Input
                    value={formData.languages}
                    onChange={(e) => setFormData({ ...formData, languages: e.target.value })}
                    className="input-styled mt-1"
                    placeholder="English, Spanish, etc."
                  />
                </div>
              </>
            )}
          </div>

          {/* Document Uploads - Advanced */}
          {advancedMode && (
            <div className="space-y-4 p-4 bg-[#D4AF37]/10 rounded-xl border border-[#D4AF37]/30">
              <h3 className="font-semibold flex items-center gap-2 text-[#D4AF37]">
                <FileText className="w-5 h-5" />
                Document Uploads
              </h3>
              
              <div className="grid md:grid-cols-3 gap-4">
                {/* ID Document */}
                <div className="p-4 border-2 border-dashed border-blue-300 rounded-lg text-center">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                  <p className="text-sm font-medium mb-2">ID Document</p>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => handleFileSelect('id_document', e.target.files[0])}
                    className="hidden"
                    id="id-upload"
                  />
                  <label htmlFor="id-upload" className="cursor-pointer">
                    {documents.id_document ? (
                      <span className="text-[var(--success)] text-sm">{documents.id_document.name}</span>
                    ) : (
                      <span className="text-[#D4AF37] text-sm hover:underline">Choose File</span>
                    )}
                  </label>
                </div>

                {/* Certification */}
                <div className="p-4 border-2 border-dashed border-blue-300 rounded-lg text-center">
                  <FileCheck className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                  <p className="text-sm font-medium mb-2">Certification</p>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => handleFileSelect('certification', e.target.files[0])}
                    className="hidden"
                    id="cert-upload"
                  />
                  <label htmlFor="cert-upload" className="cursor-pointer">
                    {documents.certification ? (
                      <span className="text-[var(--success)] text-sm">{documents.certification.name}</span>
                    ) : (
                      <span className="text-[#D4AF37] text-sm hover:underline">Choose File</span>
                    )}
                  </label>
                </div>

                {/* Background Check */}
                <div className="p-4 border-2 border-dashed border-blue-300 rounded-lg text-center">
                  <Shield className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                  <p className="text-sm font-medium mb-2">Background Check</p>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => handleFileSelect('background_check', e.target.files[0])}
                    className="hidden"
                    id="bg-upload"
                  />
                  <label htmlFor="bg-upload" className="cursor-pointer">
                    {documents.background_check ? (
                      <span className="text-[var(--success)] text-sm">{documents.background_check.name}</span>
                    ) : (
                      <span className="text-[#D4AF37] text-sm hover:underline">Choose File</span>
                    )}
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Bio - Advanced */}
          {advancedMode && (
            <div>
              <Label>Worker Bio / Notes</Label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                className="input-styled mt-1"
                placeholder="Any additional notes about this worker..."
                rows={3}
              />
            </div>
          )}

          {/* Verification */}
          <div className="p-4 bg-[var(--accent)] rounded-xl">
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={formData.is_verified}
                onCheckedChange={(checked) => setFormData({ ...formData, is_verified: checked })}
              />
              <div>
                <span className="font-semibold">Mark as Verified</span>
                <p className="text-sm text-[var(--text-muted)]">Verified workers show a badge to customers</p>
              </div>
            </label>
          </div>

          {/* Start Onboarding */}
          <div className="p-4 bg-[var(--success)]/10 border border-[var(--success)]/30 rounded-xl">
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={formData.start_onboarding}
                onCheckedChange={(checked) => setFormData({ ...formData, start_onboarding: checked })}
                data-testid="start-onboarding-checkbox"
              />
              <div>
                <span className="font-semibold text-[var(--success)]">Start Onboarding Process</span>
                <p className="text-sm text-[var(--text-muted)]">
                  Worker will need to complete required documents (Driver&apos;s License, Vehicle Registration, Insurance, etc.) and sign waivers before they can start working
                </p>
              </div>
            </label>
          </div>

          {/* Note */}
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4 text-sm">
            <p className="font-medium text-blue-800">Note:</p>
            <p className="text-[#D4AF37]">A temporary password will be generated for this worker. They can change it after logging in.</p>
          </div>

          {/* Buttons */}
          <div className="flex gap-4">
            <Button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="btn-primary flex-1" data-testid="create-worker-submit">
              {loading ? 'Creating...' : 'Create Worker'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AddWorkerModal;
